import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ForecastCol } from "./ForecastCol";
import { Tag } from "./Tag";
import { useScrollSync } from "../lib/scrollSync";
import type { EnrichedSpot, Spot, SpeedUnit } from "../lib/types";
import styles from "./SpotCard.module.css";

interface SpotCardProps {
  spot: EnrichedSpot;
  unit: SpeedUnit;
  /** Outlines the card with --border-default. Used to indicate the card
   *  matches the spot currently selected on the side map. */
  selected?: boolean | undefined;
  /** Day strip's selected day — drives the per-column selected/dimmed
   *  treatment in the forecast row. */
  selectedDay?: number;
  /** Notifies the parent when the pointer enters/leaves the card.
   *  Used by the overview to scale the corresponding map marker on
   *  desktop hover (mobile devices don't fire hover events, so the
   *  callback just no-ops there). */
  onHoverChange?: (hovered: boolean) => void;
}

export function SpotCard({
  spot,
  unit,
  selected = false,
  selectedDay,
  onHoverChange,
}: SpotCardProps) {
  const [, setLocation] = useLocation();
  // Carry the day-strip's selection through to the detail page so the
  // user lands on the same day they were just looking at on the
  // overview (especially relevant for "best wind this week", which
  // auto-pins the strip to the week's peak day). ForecastCol already
  // appends `?day=` for column taps; this keeps the card-body tap in
  // sync with that behaviour.
  const onClick = () => {
    const path =
      selectedDay !== undefined ? `/spot/${spot.id}?day=${selectedDay}` : `/spot/${spot.id}`;
    setLocation(path);
  };

  // The forecast row's day-window is driven by the day-strip's
  // chevrons (via ScrollSyncProvider), not by individual card touch
  // swipes. User-initiated horizontal scroll on a card row is
  // explicitly disabled via touch-action in the CSS — otherwise a
  // diagonal page-scroll gesture starting on a card row gets snagged
  // as a horizontal scroll, which feels broken on mobile. The
  // useScrollSync registration stays so chevron-driven programmatic
  // scrollTo() still keeps all cards in lockstep.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useScrollSync(scrollRef);

  // Track atEnd to hide the right-edge gradient overlay when fully scrolled.
  // The card is scroll-synced with siblings so all cards report atEnd together.
  const [atEnd, setAtEnd] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setAtEnd(el.scrollLeft >= max - 1 || max <= 0);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [spot.days.length]);

  return (
    <div
      className={[styles.spotCard, selected && styles.spotCardSelected]
        .filter(Boolean)
        .join(" ")}
      data-spotcard-id={spot.id}
      onClick={onClick}
      {...(onHoverChange && {
        onMouseEnter: () => onHoverChange(true),
        onMouseLeave: () => onHoverChange(false),
      })}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <span className={styles.cardArea}>{spot.area}</span>
          <span className={styles.cardName}>{spot.name}</span>
        </div>
        <div className={styles.cardHeaderRight}>
          <span className={styles.worksBestLabel}>Best in</span>
          <div className={styles.worksBestTags}>
            {spot.ideal.dirs.map((d) => (
              <Tag key={d}>{d}</Tag>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.cardForecast}>
        <div ref={scrollRef} className={styles.cardForecastContent}>
          {spot.days.map((day) => (
            <ForecastCol
              key={day.dayOffset}
              day={day}
              spotId={spot.id}
              unit={unit}
              idealDirs={spot.ideal.dirs}
              selectedDay={selectedDay}
            />
          ))}
        </div>
        <div
          className={styles.cardForecastFade}
          aria-hidden
          data-hidden={atEnd ? "true" : undefined}
        />
      </div>
    </div>
  );
}

/**
 * Loading-state SpotCard. When `spot` is provided (the common case —
 * pulled from `STATIC_SPOTS` so the page renders before `/api/forecast`
 * resolves), the header shows the real area / name / "Best in" tags
 * and only the forecast row shimmers. Without `spot`, the whole card
 * falls back to anonymous shimmer placeholders.
 */
export function SkeletonSpotCard({
  skeletonCount,
  spot,
}: {
  skeletonCount: number;
  spot?: Spot;
}) {
  return (
    <div className={[styles.spotCard, styles.spotCardSkeleton].join(" ")}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <span
            className={[styles.cardArea, !spot && styles.cardAreaSkel]
              .filter(Boolean)
              .join(" ")}
          >
            {spot?.area ?? "Nynäshamn"}
          </span>
          <span
            className={[styles.cardName, !spot && styles.cardNameSkel]
              .filter(Boolean)
              .join(" ")}
          >
            {spot?.name ?? "Spotname"}
          </span>
        </div>
        {spot && spot.ideal.dirs.length > 0 && (
          <div className={styles.cardHeaderRight}>
            <span className={styles.worksBestLabel}>Best in</span>
            <div className={styles.worksBestTags}>
              {spot.ideal.dirs.map((d) => (
                <Tag key={d}>{d}</Tag>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={styles.cardForecast}>
        <div className={styles.cardForecastContent}>
          {Array.from({ length: skeletonCount }, (_, i) => (
            <div key={i} className={styles.skeletonCol} />
          ))}
        </div>
      </div>
    </div>
  );
}
